import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";

function moneyBRL(v) {
    return Number(v || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
}

function buildInputId(prefix, productId, index) {
    return `${prefix}-${productId}-${index}`;
}

function onlyDigits(value) {
    return String(value || "").replace(/\D/g, "");
}

function normalizeLabel(value) {
    return String(value || "")
        .toLowerCase()
        .trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
}

function isDateField(label) {
    const raw = normalizeLabel(label);
    return raw.includes("data de nascimento");
}

function getSpecialDecimalConfig(label) {
    const raw = normalizeLabel(label);

    if (raw.includes("peso de nascimento")) {
        return {
            key: "birth_weight",
            decimals: 3,
            inputMode: "numeric",
        };
    }

    if (raw.includes("tamanho de nascimento")) {
        return {
            key: "birth_size",
            decimals: 2,
            inputMode: "numeric",
        };
    }

    return null;
}

function applyDateMask(value) {
    const digits = onlyDigits(value).slice(0, 8);

    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function isValidNumericDate(value) {
    const digits = onlyDigits(value);

    if (!/^\d{8}$/.test(digits)) return false;

    const day = Number(digits.slice(0, 2));
    const month = Number(digits.slice(2, 4));
    const year = Number(digits.slice(4, 8));

    if (month < 1 || month > 12) return false;
    if (year < 1900 || year > 2100) return false;

    const lastDayOfMonth = new Date(year, month, 0).getDate();
    return day >= 1 && day <= lastDayOfMonth;
}

function formatDecimalFromDigits(value, decimals) {
    const digits = onlyDigits(value);

    if (!digits) return "";

    const padded = digits.padStart(decimals + 1, "0");
    const integerPart = padded.slice(0, -decimals) || "0";
    const decimalPart = padded.slice(-decimals);

    return `${Number(integerPart)},${decimalPart}`;
}

function parseDecimalMaskedToNormalized(value, decimals) {
    const digits = onlyDigits(value);

    if (!digits) return "";

    const padded = digits.padStart(decimals + 1, "0");
    const integerPart = padded.slice(0, -decimals) || "0";
    const decimalPart = padded.slice(-decimals);

    return `${Number(integerPart)}.${decimalPart}`;
}

function isValidDecimalMasked(value) {
    const digits = onlyDigits(value);
    return digits.length > 0;
}

function formatCustomTextValue(item) {
    if (!item) return "";
    if (item.isDate) return applyDateMask(item.value);
    if (item.decimalConfig) return item.displayValue || "";
    return item.value || "";
}

function getOptionProductType(option) {
    return option?.product_type || option?.chain_type || "Padrão";
}

function getOptionDisplayImage(option, product) {
    const code = String(option?.code || "").trim().toUpperCase();

    if (option?.thumb_image) return option.thumb_image;
    if (option?.option_image_url) return option.option_image_url;

    const productImage = product?.card_image || product?.image_url || "";

    if (code === "PRATA1") return productImage;
    if (code === "OURO01") return productImage;

    return productImage;
}

function getOptionDisplayLabel(option) {
    const code = String(option?.code || "").trim().toUpperCase();

    if (code === "PRATA1") return "Prateado";
    if (code === "OURO01") return "Dourado";

    return option?.color || getOptionProductType(option);
}

export default function ProductCatalogCard({ product, onAddToCart, closeOptionsSignal }) {
    const [selectedOptionId, setSelectedOptionId] = useState(null);

    const textInputs = product?.frontend_config?.text_inputs || [];
    const extraOptions = product?.extra_options || [];
    const allowPhoto = Boolean(product?.allow_photo);

    const selectedOption = useMemo(() => {
        return product?.options?.find((item) => item.id === selectedOptionId) || null;
    }, [product, selectedOptionId]);

    const displayPrice = selectedOption?.price || product?.base_price || 0;

    useEffect(() => {
        setSelectedOptionId(null);
    }, [closeOptionsSignal]);

    function handleSelectOption(optionId) {
        setSelectedOptionId((prev) => (prev === optionId ? null : optionId));
    }

    function buildModalHtml(option) {
        const optionImage = getOptionDisplayImage(option, product);
        const optionLabel = getOptionDisplayLabel(option);

        const textInputsHtml = textInputs
            .map((input, index) => {
                const label = input?.placeholder || `Campo ${index + 1}`;
                const isDate = isDateField(label);
                const decimalConfig = getSpecialDecimalConfig(label);

                const placeholder = isDate
                    ? "dd/mm/aaaa"
                    : decimalConfig
                        ? decimalConfig.decimals === 3
                            ? "0,000"
                            : "0,00"
                        : label;

                const maxDigits = Number(input?.max_length || 4);

                const maxLength = isDate
                    ? 10
                    : decimalConfig
                        ? maxDigits + decimalConfig.decimals + 3
                        : (input?.max_length || 50);

                const inputId = buildInputId("pp-input", product.id, index);

                return `
                    <div style="display:grid;gap:6px;margin-bottom:12px;">
                        <label for="${inputId}" style="font-size:12px;font-weight:800;color:#6b7280;">
                            ${label}
                            <span style="color:#9ca3af;font-weight:700;">(máx. ${input?.max_length || (isDate ? 8 : 50)})</span>
                        </label>
                        <input
                            id="${inputId}"
                            type="text"
                            maxlength="${maxLength}"
                            placeholder="${placeholder}"
                            data-type="${isDate ? "date" : decimalConfig ? decimalConfig.key : "text"}"
                            data-decimals="${decimalConfig?.decimals || ""}"
                            inputmode="${isDate ? "numeric" : decimalConfig?.inputMode || "text"}"
                            style="
                                width:100%;
                                border-radius:10px;
                                border:1px solid rgba(17,24,39,0.14);
                                padding:10px 12px;
                                font-weight:700;
                                box-sizing:border-box;
                                outline:none;
                            "
                        />
                    </div>
                `;
            })
            .join("");

        const extraOptionsHtml = extraOptions.length
            ? `
                <div style="margin-top:4px;">
                    <div style="font-size:12px;font-weight:900;color:#111827;margin-bottom:8px;">
                        Opções
                    </div>
                    <div style="display:flex;gap:8px;flex-wrap:wrap;">
                        ${extraOptions
                .map(
                    (optionLabel) => `
                                    <label style="
                                        display:inline-flex;
                                        align-items:center;
                                        gap:6px;
                                        padding:8px 10px;
                                        border-radius:999px;
                                        border:1px solid rgba(17,24,39,0.12);
                                        background:#fff;
                                        font-size:12px;
                                        font-weight:700;
                                        color:#111827;
                                        cursor:pointer;
                                    ">
                                        <input type="radio" name="pp-extra-option-${product.id}" value="${optionLabel}" />
                                        <span>${optionLabel}</span>
                                    </label>
                                `
                )
                .join("")}
                    </div>
                </div>
            `
            : "";

        const uploadHtml = allowPhoto
            ? `
                <div style="margin-top:14px;">
                    <div style="font-size:12px;font-weight:900;color:#111827;margin-bottom:8px;">
                        Enviar foto
                    </div>
                    <input
                        id="pp-file-${product.id}"
                        type="file"
                        accept="image/*"
                        style="width:100%;"
                    />
                </div>
            `
            : "";

        return `
            <div style="text-align:left;">
                <div style="
                    display:flex;
                    gap:12px;
                    align-items:center;
                    padding:12px;
                    border-radius:14px;
                    background:rgba(144,35,72,0.05);
                    border:1px solid rgba(144,35,72,0.10);
                    margin-bottom:14px;
                ">
                    <div style="
                        width:64px;
                        height:84px;
                        border-radius:12px;
                        overflow:hidden;
                        background:#f5f5f5;
                        flex-shrink:0;
                        display:flex;
                        align-items:center;
                        justify-content:center;
                    ">
                        ${optionImage
                ? `<img src="${optionImage}" alt="${option?.code || optionLabel}" style="width:100%;height:100%;object-fit:contain;display:block;background:#f5f5f5;" />`
                : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;color:#6b7280;padding:6px;text-align:center;">${optionLabel || "Opção"}</div>`
            }
                    </div>

                    <div style="min-width:0;">
                        <div style="font-size:16px;font-weight:900;color:#111827;">
                            ${product.name || "-"}
                        </div>
                        <div style="font-size:12px;font-weight:700;color:#6b7280;margin-top:3px;">
                            ${getOptionProductType(option)} • ${optionLabel || "-"}
                        </div>
                        <div style="font-size:12px;font-weight:700;color:#6b7280;margin-top:2px;">
                            ${option?.code || "-"}
                        </div>
                        <div style="font-size:17px;font-weight:900;color:#902348;margin-top:6px;">
                            ${moneyBRL(option?.price || 0)}
                        </div>
                    </div>
                </div>

                ${textInputsHtml}
                ${extraOptionsHtml}
                ${uploadHtml}

                <div style="margin-top:14px;">
                    <div style="font-size:12px;font-weight:900;color:#111827;margin-bottom:8px;">
                        Quantidade
                    </div>
                    <input
                        id="pp-qty-${product.id}"
                        type="number"
                        min="1"
                        value="1"
                        style="
                            width:100%;
                            max-width:110px;
                            border-radius:10px;
                            border:1px solid rgba(17,24,39,0.14);
                            padding:10px 12px;
                            font-weight:800;
                            box-sizing:border-box;
                            outline:none;
                        "
                    />
                </div>
            </div>
        `;
    }

    async function openCustomizationModal(option) {
        const result = await Swal.fire({
            title: "Personalizar produto",
            html: buildModalHtml(option),
            width: 640,
            padding: "22px",
            showCancelButton: true,
            confirmButtonText: "Confirmar",
            cancelButtonText: "Voltar",
            confirmButtonColor: "#902348",
            cancelButtonColor: "#BDBDBD",
            focusConfirm: false,
            didOpen: () => {
                const popup = Swal.getPopup();
                if (!popup) return;

                textInputs.forEach((input, index) => {
                    const inputEl = popup.querySelector(
                        `#${buildInputId("pp-input", product.id, index)}`
                    );
                    if (!inputEl) return;

                    const type = inputEl.getAttribute("data-type");
                    const decimals = Number(inputEl.getAttribute("data-decimals") || 0);

                    inputEl.dataset.lastValue = inputEl.value || "";
                    inputEl.dataset.rawDigits = "";

                    inputEl.addEventListener("input", (e) => {
                        if (type === "date") {
                            inputEl.value = applyDateMask(inputEl.value);
                            inputEl.dataset.lastValue = inputEl.value;
                            return;
                        }

                        if (type === "birth_weight" || type === "birth_size") {
                            const maxDigits = Number(input?.max_length || 4);
                            const insertedText = String(e?.data || "").replace(/\D/g, "");
                            const isDeleting =
                                e?.inputType === "deleteContentBackward" ||
                                e?.inputType === "deleteContentForward";

                            let rawDigits = String(inputEl.dataset.rawDigits || "");

                            if (isDeleting) {
                                rawDigits = rawDigits.slice(0, -1);
                            } else if (e?.inputType === "insertFromPaste") {
                                const pastedDigits = onlyDigits(inputEl.value);
                                rawDigits = pastedDigits.slice(0, maxDigits);
                            } else if (insertedText) {
                                if (rawDigits.length >= maxDigits) {
                                    inputEl.value =
                                        inputEl.dataset.lastValue || formatDecimalFromDigits(rawDigits, decimals);
                                    return;
                                }
                                rawDigits = (rawDigits + insertedText).slice(0, maxDigits);
                            } else {
                                rawDigits = onlyDigits(inputEl.value).slice(0, maxDigits);
                            }

                            inputEl.dataset.rawDigits = rawDigits;
                            inputEl.value = formatDecimalFromDigits(rawDigits, decimals);
                            inputEl.dataset.lastValue = inputEl.value;
                            return;
                        }

                        const maxLength = input?.max_length || 50;
                        if (inputEl.value.length > maxLength) {
                            inputEl.value = inputEl.value.slice(0, maxLength);
                        }
                        inputEl.dataset.lastValue = inputEl.value;
                    });
                });
            },

            preConfirm: () => {
                const popup = Swal.getPopup();
                if (!popup) return false;

                const customTexts = textInputs.map((input, index) => {
                    const inputEl = popup.querySelector(
                        `#${buildInputId("pp-input", product.id, index)}`
                    );

                    const label = input?.placeholder || `Campo ${index + 1}`;
                    const isDate = isDateField(label);
                    const decimalConfig = getSpecialDecimalConfig(label);

                    let rawValue = String(inputEl?.value || "").trim();
                    let value = rawValue;
                    let displayValue = rawValue;
                    let normalizedValue = rawValue;

                    if (isDate) {
                        value = onlyDigits(rawValue).slice(0, 8);
                        displayValue = applyDateMask(value);
                        normalizedValue = value;
                    } else if (decimalConfig) {
                        const maxDigits = Number(input?.max_length || 4);
                        const digitsFromDataset = String(inputEl?.dataset?.rawDigits || "");
                        const digits = digitsFromDataset
                            ? digitsFromDataset.slice(0, maxDigits)
                            : onlyDigits(rawValue).slice(0, maxDigits);

                        value = digits;
                        displayValue = formatDecimalFromDigits(digits, decimalConfig.decimals);
                        normalizedValue = parseDecimalMaskedToNormalized(
                            displayValue,
                            decimalConfig.decimals
                        );
                    }

                    return {
                        index,
                        label,
                        value,
                        displayValue,
                        normalizedValue,
                        isDate,
                        decimalConfig,
                        max_length: isDate ? 8 : (input?.max_length || null),
                    };
                });

                const missingField = customTexts.find(
                    (field) => !String(field.value || "").trim()
                );

                if (missingField) {
                    Swal.showValidationMessage(
                        `Preencha o campo "${missingField.label}".`
                    );
                    return false;
                }

                const invalidDate = customTexts.find((field) => {
                    if (!field.value) return false;
                    if (field.isDate) {
                        return !isValidNumericDate(field.value);
                    }
                    return false;
                });

                if (invalidDate) {
                    Swal.showValidationMessage(
                        `Data inválida em "${invalidDate.label}". Use o formato dd/mm/aaaa.`
                    );
                    return false;
                }

                const invalidDecimalField = customTexts.find((field) => {
                    if (!field?.decimalConfig) return false;
                    return !isValidDecimalMasked(field.displayValue);
                });

                if (invalidDecimalField) {
                    Swal.showValidationMessage(
                        `Preencha corretamente o campo "${invalidDecimalField.label}".`
                    );
                    return false;
                }

                let selectedExtraOption = "";

                if (extraOptions.length) {
                    const selectedExtraRadio = popup.querySelector(
                        `input[name="pp-extra-option-${product.id}"]:checked`
                    );

                    if (!selectedExtraRadio) {
                        Swal.showValidationMessage("Selecione uma das opções disponíveis.");
                        return false;
                    }

                    selectedExtraOption = selectedExtraRadio.value;
                }

                let selectedPhoto = null;

                if (allowPhoto) {
                    const fileEl = popup.querySelector(`#pp-file-${product.id}`);
                    selectedPhoto = fileEl?.files?.[0] || null;

                    if (!selectedPhoto) {
                        Swal.showValidationMessage("Envie uma foto para continuar.");
                        return false;
                    }
                }

                const qtyEl = popup.querySelector(`#pp-qty-${product.id}`);
                const quantity = Math.max(1, Number(qtyEl?.value || 1));

                return {
                    quantity,
                    customTexts,
                    customText: customTexts
                        .map((item) => formatCustomTextValue(item))
                        .filter(Boolean)
                        .join(" • "),
                    selectedExtraOption,
                    photoName: selectedPhoto?.name || "",
                    selectedPhoto,
                };
            },
        });

        if (!result.isConfirmed || !result.value) return;

        const payload = result.value;
        const productType = getOptionProductType(option);

        onAddToCart({
            productId: product.id,
            productName: product.name,
            description: product.description || "",
            optionId: option.id,
            code: option.code,
            productType,
            productTypeCode: option?.product_type_code || "",
            color: getOptionDisplayLabel(option),
            price: option.price,
            quantity: payload.quantity,
            customTexts: payload.customTexts,
            customText: payload.customText,
            selectedExtraOption: payload.selectedExtraOption,
            allowPhoto,
            photoName: payload.photoName,
            selectedPhoto: payload.selectedPhoto,
            chainType: productType,
        });
    }

    return (
        <div style={styles.card}>
            <div style={styles.imageWrap}>
                {product.card_image ? (
                    <img src={product.card_image} alt={product.name} style={styles.image} />
                ) : (
                    <div style={styles.imageFallback}>Personalizado</div>
                )}
            </div>

            <div style={styles.content}>
                <div style={styles.title}>{product.name}</div>

                <div style={styles.groupCode}>
                    Código: {product.group_code || product.id || "-"}
                </div>

                <div style={styles.description}>
                    {product.description || "Produto personalizado disponível para montagem."}
                </div>

                <div style={{ ...styles.priceLine, marginTop: "auto" }}>
                    <span style={styles.pricePrefix}>
                        {selectedOption ? "Selecionado:" : "A partir de"}
                    </span>
                    <span style={styles.priceValue}>{moneyBRL(displayPrice)}</span>
                </div>

                <div style={styles.optionsGrid}>
                    {(product.options || []).map((option) => {
                        const isSelected = selectedOptionId === option.id;
                        const productType = getOptionProductType(option);
                        const optionImage = getOptionDisplayImage(option, product);
                        const optionLabel = getOptionDisplayLabel(option);

                        return (
                            <button
                                key={option.id}
                                type="button"
                                onClick={() => handleSelectOption(option.id)}
                                style={{
                                    ...styles.optionCard,
                                    ...(isSelected ? styles.optionCardSelected : {}),
                                }}
                            >
                                <div style={styles.optionThumbWrap}>
                                    {optionImage ? (
                                        <img
                                            src={optionImage}
                                            alt={`${productType} ${optionLabel || ""}`}
                                            style={styles.optionThumb}
                                        />
                                    ) : (
                                        <div style={styles.optionThumbFallback}>
                                            {optionLabel || "Opção"}
                                        </div>
                                    )}
                                </div>

                                <div style={styles.optionName}>{optionLabel}</div>
                                <div style={styles.optionSub}>{productType}</div>
                                <div style={styles.optionPrice}>{moneyBRL(option.price)}</div>
                            </button>
                        );
                    })}
                </div>

                <button
                    type="button"
                    onClick={() => {
                        if (!selectedOption) return;
                        openCustomizationModal(selectedOption);
                    }}
                    disabled={!selectedOption}
                    style={{
                        ...styles.personalizeBtn,
                        ...(!selectedOption ? styles.personalizeBtnDisabled : {}),
                    }}
                >
                    {selectedOption ? "Personalizar e adicionar" : "Selecione uma cor"}
                </button>
            </div>
        </div>
    );
}

const styles = {
    card: {
        background: "#fff",
        borderRadius: 14,
        overflow: "hidden",
        border: "1px solid rgba(17,24,39,0.08)",
        boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minWidth: 0,
    },

    // imagem principal do produto
    imageWrap: {
        width: "100%",
        background: "#f5f5f5",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        padding: 0,
        boxSizing: "border-box",
        aspectRatio: "3 / 4", // mantém retangular sem precisar altura fixa
    },
    image: {
        width: "100%",
        height: "100%",
        objectFit: "contain", // mantém imagem inteira, sem cortar
        display: "block",
        background: "#f5f5f5",
    },
    imageFallback: {
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, rgba(144,35,72,0.08), rgba(181,61,103,0.14))",
        color: "#902348",
        fontWeight: 900,
        fontSize: 18,
    },

    content: {
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        flex: 1,
        minHeight: 0
    },
    title: {
        fontSize: 15,
        fontWeight: 900,
        color: "#111827",
        lineHeight: 1.25,
        minHeight: 60,
    },
    groupCode: {
        fontSize: 11,
        fontWeight: 800,
        color: "#902348",
        minHeight: 16,
    },
    description: {
        fontSize: 12,
        lineHeight: 1.4,
        color: "#4b5563",
        fontWeight: 500,
        minHeight: 118,
    },
    priceLine: {
        display: "flex",
        gap: 6,
        alignItems: "baseline",
        flexWrap: "wrap",
    },
    pricePrefix: {
        fontSize: 12,
        color: "#6b7280",
        fontWeight: 700,
    },
    priceValue: {
        fontSize: 17,
        fontWeight: 900,
        color: "#111827",
    },

    // opções sempre em 2 colunas
    optionsGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 8,
    },
    optionCard: {
        border: "1px solid rgba(17,24,39,0.08)",
        borderRadius: 12,
        padding: 8,
        background: "#fff",
        textAlign: "left",
        cursor: "pointer",
        transition: "all 180ms ease",
        minWidth: 0,
    },
    optionCardSelected: {
        border: "2px solid #902348",
        background: "rgba(144,35,72,0.05)",
        transform: "scale(1.01)",
        boxShadow: "0 6px 14px rgba(144,35,72,0.10)",
    },

    // thumb da opção sem corte
    optionThumbWrap: {
        width: "100%",
        borderRadius: 8,
        overflow: "hidden",
        background: "#f5f5f5",
        marginBottom: 6,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
        boxSizing: "border-box",
        aspectRatio: "3 / 4",
    },
    optionThumb: {
        width: "100%",
        height: "100%",
        objectFit: "contain", // mantém tamanho/orientação real
        display: "block",
        background: "#f5f5f5",
    },
    optionThumbFallback: {
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#6b7280",
        fontSize: 11,
        fontWeight: 800,
        textAlign: "center",
        padding: 6,
        boxSizing: "border-box",
    },
    optionName: {
        fontWeight: 800,
        fontSize: 12,
        color: "#111827",
    },
    optionSub: {
        fontSize: 11,
        color: "#6b7280",
        fontWeight: 700,
        marginTop: 2,
    },
    optionPrice: {
        marginTop: 4,
        fontSize: 13,
        fontWeight: 900,
        color: "#111827",
    },
    personalizeBtn: {
        // marginTop: "auto",
        border: "none",
        borderRadius: 10,
        padding: "11px 12px",
        background: "green",
        color: "#fff",
        fontWeight: 900,
        cursor: "pointer",
    },
    personalizeBtnDisabled: {
        opacity: 0.45,
        cursor: "not-allowed",
        background: "grey",
    },
};